import { Eye, Layers, Wrench, BookOpen } from "lucide-react";
import { type MultipliersResponse } from "@/api/multipliers";
import { KpiSection } from "./KpiSection";
import { KpiModelOverview } from "./KpisModelOverview";
import { KpiScoringSystem } from "./KpiScoringSystem";
import { KpisComponents } from "./KpisComponents";
import { KpisCalculationMethods } from "./KpisCalculationMethods";
import { KpisTimeOnIceExplanation } from "./KpisTimeOnIceExplanation";
import { KpiZScoreInterpretation } from "./KpiZScoreInterpretation";

interface KpiMethodologyLayoutProps {
    data: MultipliersResponse;
    isDefault?: boolean;
}

export const KpiMethodologyLayout = ({ data, isDefault = false }: KpiMethodologyLayoutProps) => {
    return (
        <div className="space-y-4">
            <KpiSection
                title="The Big Picture"
                subtitle="Understanding the Score"
                icon={<Eye className="w-5 h-5" />}
                color="var(--color-general-blue)"
                defaultOpen
            >
                <KpiModelOverview />
                <KpiScoringSystem />
            </KpiSection>

            <KpiSection
                title="The Ingredients"
                subtitle="What Contributes to the Score"
                icon={<Layers className="w-5 h-5" />}
                color="var(--color-general-orange)"
            >
                <KpisComponents data={data} isDefault={isDefault} />

                <div className="space-y-4">
                    <h3 className="text-base font-headline font-bold text-on-surface">Special Component Rules</h3>

                    <div className="bg-surface-container-lowest rounded-xl shadow-sm px-5 py-4" style={{ borderLeft: "4px solid var(--color-general-orange)" }}>
                        <p className="text-sm font-semibold text-on-surface mb-1">"Soft Cap" and Exceptional Performances</p>
                        <p className="text-sm text-on-surface-variant">
                            Due to the mathematical nature of this methodology, the scoring system naturally oscillates
                            within a predictable range, typically between 0 and 2 × the average value (which aligns with
                            the max_points). If a player delivers an outstanding, anomalous performance where their
                            z-score exceeds the defined weight threshold (Z &gt; W), the mathematical output will
                            gracefully exceed the theoretical max_points for that category. This ensures that historic
                            or simply extraordinary performances are fully recognized and rewarded.
                        </p>
                    </div>

                    <div className="bg-surface-container-lowest rounded-xl shadow-sm px-5 py-4" style={{ borderLeft: "4px solid var(--color-general-orange)" }}>
                        <p className="text-sm font-semibold text-on-surface mb-1">Bonus Breakdown</p>
                        <p className="text-sm text-on-surface-variant">
                            A player can earn up to 10 additional points through goals and assists. Each goal scored
                            grants +7.5 points, while each assist provides +5 points. These points are counted
                            separately, meaning that a player can still achieve the maximum total of 100 points even
                            without scoring a goal or registering an assist.
                        </p>
                    </div>

                    <div className="bg-surface-container-lowest rounded-xl shadow-sm px-5 py-4" style={{ borderLeft: "4px solid var(--color-general-cyan)" }}>
                        <p className="text-sm font-semibold text-on-surface mb-1">Uncapped Components</p>
                        <p className="text-sm text-on-surface-variant">
                            Some metrics may have no defined maximum points cap, meaning there is no upper limit to how
                            many points a player can earn from that metric. These are displayed with an infinity symbol
                            (∞). The total category score may also become uncapped when it contains any uncapped metric.
                        </p>
                    </div>
                </div>
            </KpiSection>

            <KpiSection
                title="The Mechanics"
                subtitle="How Actions Translate to Points"
                icon={<Wrench className="w-5 h-5" />}
                color="var(--color-general-green)"
            >
                <KpisCalculationMethods />

                <div className="bg-surface-container-lowest rounded-xl shadow-sm px-5 py-4" style={{ borderLeft: "4px solid var(--color-general-green)" }}>
                    <p className="text-sm font-semibold text-on-surface mb-1">Discrete Events and Flat Modifiers</p>
                    <p className="text-sm text-on-surface-variant">
                        To capture specific, impactful match actions, the model incorporates discrete event-based
                        modifiers that bypass the standard z-score normalization. Certain situational events apply flat
                        positive or negative adjustments per occurrence — for example, +1 point for a blocked shot or -1
                        point for conceding a shot from a dangerous area. This means a player's overall rating can
                        dynamically expand (or drop) based on the sheer volume of these critical, raw actions,
                        regardless of their performance in the core metrics.
                    </p>
                </div>
            </KpiSection>

            <KpiSection
                title="The Fine Print"
                subtitle="Advanced Statistics & Constraints"
                icon={<BookOpen className="w-5 h-5" />}
                color="var(--color-general-purple)"
            >
                <KpisTimeOnIceExplanation />
                <KpiZScoreInterpretation />
            </KpiSection>
        </div>
    );
};
